import { Controller } from "@hotwired/stimulus"

// Índice de avance de los formularios de recursos y unidades. Arma la lista
// desde las tarjetas de paso presentes y recalcula el estado de cada sección
// con cada tecla o cambio:
//   lista       todos los requeridos válidos (o hay contenido, si no tiene)
//   incompleta  algo escrito pero faltan requeridos
//   pendiente   sin tocar
const ESTADOS = { lista: "Listo", incompleta: "A medias", pendiente: "Pendiente" }

export default class extends Controller {
  static targets = ["lista", "seccion"]

  connect() {
    this.construir()
    this.revisar()
  }

  disconnect() {
    clearTimeout(this.timeout)
  }

  construir() {
    this.listaTarget.innerHTML = ""
    this.items = this.seccionTargets.map((seccion) => {
      const boton = document.createElement("button")
      boton.type = "button"
      boton.className = "indice-paso"
      boton.innerHTML = `<span class="indice-punto" aria-hidden="true">
                           <svg viewBox="0 0 12 12" width="10" height="10" fill="none"
                                stroke="currentColor" stroke-width="2.5"
                                stroke-linecap="round" stroke-linejoin="round">
                             <path d="M2 6.5 4.8 9 10 3.5"/>
                           </svg>
                         </span>
                         <span class="min-w-0 truncate">${seccion.dataset.titulo}</span>`
      boton.addEventListener("click", () => {
        seccion.scrollIntoView({ behavior: "smooth", block: "start" })
      })

      const item = document.createElement("li")
      item.appendChild(boton)
      this.listaTarget.appendChild(item)
      return { seccion, boton, punto: boton.querySelector(".indice-punto") }
    })
  }

  // Mientras la IA formatea un campo, su paso late en violeta (evento
  // burbujeante ia:paso disparado por formato-ia).
  pulsar(evento) {
    const item = this.items?.find(({ seccion }) => seccion.contains(evento.target))
    item?.punto.classList.toggle("ia-pulso", evento.detail.estado === "pulso")
  }

  // Cuando los títulos de los pasos cambian en vivo (segun-formato), la
  // lista se rearma para reflejarlos.
  reconstruir() {
    this.construir()
    this.revisar()
  }

  // Las altas/bajas de filas anidadas y los componentes llegan como clicks o
  // eventos sintéticos seguidos: un respiro corto evita recalcular por tecla.
  revisarPronto() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.revisar(), 150)
  }

  revisar() {
    this.items?.forEach(({ seccion, boton, punto }) => {
      const estado = this.estadoDe(seccion)
      boton.dataset.estado = estado
      punto.dataset.estado = estado
      boton.title = `${seccion.dataset.titulo}: ${ESTADOS[estado]}`
    })
  }

  estadoDe(seccion) {
    const campos = this.camposDe(seccion)
    const requeridos = campos.filter((campo) => campo.requerido)
    const validos = requeridos.filter((campo) => campo.valido)
    const conContenido = campos.some((campo) => campo.lleno) || this.persistidos(seccion)

    if (requeridos.length > 0) {
      if (validos.length === requeridos.length) return "lista"
      return conContenido ? "incompleta" : "pendiente"
    }
    return conContenido ? "lista" : "pendiente"
  }

  // Descriptores {requerido, lleno, valido} de los campos reales de la
  // sección. Los radios se evalúan por grupo; de los componentes cuenta su
  // valor de verdad (el hidden del editor Quill y del combobox).
  camposDe(seccion) {
    const descriptores = []
    const grupos = new Map()

    seccion.querySelectorAll("input, select, textarea").forEach((campo) => {
      if (["submit", "button"].includes(campo.type) || campo.disabled) return
      if (campo.closest("dialog")) return                       // mini-form de la pauta
      if (campo.getAttribute("role") === "combobox") return     // input visible: cuenta su hidden
      if (campo.type === "hidden" &&
          campo.dataset.richTextTarget !== "input" &&
          campo.dataset.comboboxTarget !== "hidden") return

      // Requerido nativo (los chips) o marcado por shared/_field con
      // data-indice-requerido (campos que validan en el servidor).
      const requerido = campo.required || "indiceRequerido" in campo.dataset

      if (campo.type === "radio") {
        const grupo = grupos.get(campo.name) || { requerido: false, lleno: false }
        grupo.requerido ||= requerido
        grupo.lleno ||= campo.checked
        grupo.valido = grupo.lleno
        grupos.set(campo.name, grupo)
        return
      }

      const lleno = campo.type === "file" ? campo.files.length > 0 : campo.value.trim() !== ""
      descriptores.push({
        requerido: requerido,
        lleno: lleno,
        valido: campo.required ? campo.checkValidity() : lleno
      })
    })

    return descriptores.concat([...grupos.values()])
  }

  // En edición, las filas anidadas ya guardadas (links, adjuntos) traen su id
  // en un hidden: son contenido aunque sus inputs visibles estén vacíos.
  persistidos(seccion) {
    return [...seccion.querySelectorAll("input[type='hidden'][name*='[id]']")]
      .some((campo) => campo.value !== "" && !campo.closest(".hidden"))
  }
}
